import { createApiInstance } from './ApiConfig';

// Create axios instance with base configuration plus specific settings
const api = createApiInstance({
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Credentials': 'true',
  },
});

// List of all available config paths
export const CONFIG_PATHS = [
  'gnss/ntrip_client',
  'gnss/nmea_stream',
  'gnss/gnss_ext_cfg',
  'network/wifi/base',
  'network/wifi/ip',
  'network/ethernet/ip',
  'network/network_bridge/base',
  'network/network_bridge/ip',
  'network/cellular',
  'network/nat',
  'gnss/antenna_location',
  'bluetooth',
  'misc',
  'can/0',
  'can/1',
  'can/2',
];

// API functions
export const getAllDevices = () => {
  return api.get('/devices/get_all_devices');
};

export const checkIsOnline = serialNumber => {
  return api.get(`/devices/${serialNumber}/check_is_online`);
};

export const getAllNotifications = (page = 1, pageSize = 20) => {
  return api.get(`/devices/get_all_notification?page=${page}&page_size=${pageSize}`);
};

export const getDeviceNotifications = (serialNumber, page = 1, pageSize = 20) => {
  return api.get(`/devices/${serialNumber}/notifications?page=${page}&page_size=${pageSize}`);
};

// Single status entry (+ previous entry for diffing and the list of
// available timestamps) instead of the whole status history.
export const getDeviceStatusEntry = (serialNumber, timestamp = 'latest') => {
  return api.get(`/devices/${serialNumber}/status/entry?timestamp=${timestamp}`);
};

export const getTaskInfo = serialNumber => {
  return api.get(`/devices/${serialNumber}/status/task_info`);
};

export const getMemInfo = serialNumber => {
  return api.get(`/devices/${serialNumber}/status/mem_info`);
};

export const getBluetoothInfo = serialNumber => {
  return api.get(`/devices/${serialNumber}/status/bluetooth_info`);
};

// Network usage totals (today, this month, all time) plus per-day or
// per-month buckets for the requested UTC date range.
export const getNetworkUsage = (serialNumber, { startDate, endDate, granularity = 'day' } = {}) => {
  return api.get(`/devices/${serialNumber}/network_usage`, {
    params: { start_date: startDate, end_date: endDate, granularity },
  });
};

export const getDeviceConfig = (serialNumber, configPath) => {
  return api.get(`/devices/${serialNumber}/config/${configPath}`);
};

export const setDeviceConfig = (serialNumber, configPath, configData) => {
  return api.post(`/devices/${serialNumber}/config/${configPath}?source=web`, configData);
};

export const getUnsyncedConfigs = serialNumber => {
  return api.get(`/devices/${serialNumber}/configs/unsynced`);
};

export const getDeviceLogs = serialNumber => {
  return api.get(`/devices/${serialNumber}/logs/list`);
};

export const getLogFile = (serialNumber, filename) => {
  return api.get(`/devices/${serialNumber}/logs/file/${filename}`, {
    responseType: 'text',
  });
};

export const sendDeviceCommand = (serialNumber, command) => {
  return api.post(`/devices/${serialNumber}/command/${command}`);
};

// Parks the CAN strings in the backend and tells the hub to fetch and transmit them.
export const sendCanFrames = (serialNumber, bus, frames) => {
  return api.post(`/devices/${serialNumber}/can_tx`, { bus, frames });
};

// Machine Management API
export const getAllMachines = () => {
  return api.get('/machines');
};

export const getMachine = machineId => {
  return api.get(`/machines/${machineId}`);
};

export const createMachine = data => {
  return api.post('/machines', data);
};

export const updateMachine = (machineId, data) => {
  return api.put(`/machines/${machineId}`, data);
};

export const deleteMachine = machineId => {
  return api.delete(`/machines/${machineId}`);
};

export const getAvailableMachineSerials = () => {
  return api.get('/machines/available_serials');
};

export const getCompanies = () => {
  return api.get('/companies');
};

export const updateCompanyTag = (companyId, tag) => {
  return api.put(`/companies/${companyId}/tag`, { tag });
};

export const getUserCloudSyncLog = () => {
  return api.get('/user_cloud_sync/log');
};

export const deleteUserCloudSyncLog = () => {
  return api.delete('/user_cloud_sync/log');
};

export const runUserCloudSync = () => {
  return api.post('/user_cloud_sync/run');
};

export const runUserCloudIncrementalSync = () => {
  return api.post('/user_cloud_sync/run_incremental');
};

// One-shot aggregate for all machines: online/in-operation state,
// untaken incident count and last known location in a single request.
export const getMachinesOverview = () => {
  return api.get('/machines/overview');
};

export const getErrorDetectConfig = serialNumber => {
  return api.get(`/devices/${serialNumber}/error_detect_config`);
};

export const setErrorDetectConfig = (serialNumber, configData) => {
  return api.post(`/devices/${serialNumber}/error_detect_config`, configData);
};

// Incidents API
export const getIncidents = (page = 1, pageSize = 20, statusFilter = null, machineId = null) => {
  const params = new URLSearchParams({ page, page_size: pageSize });
  if (statusFilter) {
    params.append('status_filter', statusFilter);
  }
  if (machineId) {
    params.append('machine_id', machineId);
  }
  return api.get(`/incidents?${params.toString()}`);
};

export const updateIncidentStatus = (incidentId, status) => {
  return api.patch(`/incidents/${incidentId}/status`, { status });
};

export const takeIncident = incidentId => {
  return api.post(`/incidents/${incidentId}/take`);
};

export const solveIncident = incidentId => {
  return api.post(`/incidents/${incidentId}/solve`);
};

export const solveAllIncidents = machineId => {
  return api.post('/incidents/solve-all', { machine_id: machineId });
};

export const updateIncidentNotes = (incidentId, notes) => {
  return api.patch(`/incidents/${incidentId}/notes`, { notes });
};

// Machine Notes API
export const getNotesByMachine = machineId => {
  return api.get(`/notes/machine/${machineId}`);
};

export const createNote = formData => {
  return api.post('/notes', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const updateNote = (noteId, content) => {
  const formData = new FormData();
  formData.append('content', content);
  return api.put(`/notes/${noteId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const addNoteImages = (noteId, formData) => {
  return api.post(`/notes/${noteId}/images`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteNote = noteId => {
  return api.delete(`/notes/${noteId}`);
};

export const deleteNoteImage = (noteId, imageFilename) => {
  return api.delete(`/notes/${noteId}/images/${encodeURIComponent(imageFilename)}`);
};

export const getNoteImage = (machineId, noteId, imageFilename) => {
  return api.get(
    `/notes/machine/${machineId}/${noteId}/images/${encodeURIComponent(imageFilename)}`,
    {
      responseType: 'blob',
    }
  );
};

export const getServiceSummary = ({ startDate, endDate, machineIds = null }) => {
  const params = [
    `start_date=${encodeURIComponent(startDate)}`,
    `end_date=${encodeURIComponent(endDate)}`,
  ];
  if (machineIds && machineIds.length > 0) {
    params.push(`machine_ids=${encodeURIComponent(machineIds.join(','))}`);
  }
  return api.get(`/notes/service_summary?${params.join('&')}`);
};

// Machine usage tracking APIs
export const getMachineUsageSessions = (machineId, startTime = null, endTime = null) => {
  let url = `/machines/${machineId}/usage_sessions`;
  const params = [];
  if (startTime) {
    params.push(`start_time=${encodeURIComponent(startTime)}`);
  }
  if (endTime) {
    params.push(`end_time=${encodeURIComponent(endTime)}`);
  }
  if (params.length > 0) {
    url += '?' + params.join('&');
  }
  return api.get(url);
};

// Machine IMU installation APIs
export const getMachineImuInstallations = machineId => {
  return api.get(`/machines/${machineId}/imu_installations`);
};

export const createMachineImuInstallation = (machineId, data) => {
  return api.post(`/machines/${machineId}/imu_installations`, data);
};

export const updateMachineImuInstallation = (machineId, oldImuId, data) => {
  return api.put(`/machines/${machineId}/imu_installations/${oldImuId}`, data);
};

export const deleteMachineImuInstallation = (machineId, imuId) => {
  return api.delete(`/machines/${machineId}/imu_installations/${imuId}`);
};

export const getMachineErrorSessions = (machineId, startTime = null, endTime = null) => {
  let url = `/machines/${machineId}/error_sessions`;
  const params = [];
  if (startTime) {
    params.push(`start_time=${encodeURIComponent(startTime)}`);
  }
  if (endTime) {
    params.push(`end_time=${encodeURIComponent(endTime)}`);
  }
  if (params.length > 0) {
    url += '?' + params.join('&');
  }
  return api.get(url);
};

// Aggregated session summary APIs
export const getUsageSummary = ({
  months = 3,
  startDate = null,
  endDate = null,
  machineIds = null,
} = {}) => {
  const params = [];
  if (startDate && endDate) {
    params.push(`start_date=${encodeURIComponent(startDate)}`);
    params.push(`end_date=${encodeURIComponent(endDate)}`);
  } else {
    params.push(`months=${months}`);
  }
  if (machineIds && machineIds.length > 0) {
    params.push(`machine_ids=${encodeURIComponent(machineIds.join(','))}`);
  }
  return api.get(`/usage_sessions/summary?${params.join('&')}`);
};

export const getErrorSummary = ({
  months = 3,
  startDate = null,
  endDate = null,
  machineIds = null,
  excludeErrorTypes = null,
} = {}) => {
  const params = [];
  if (startDate && endDate) {
    params.push(`start_date=${encodeURIComponent(startDate)}`);
    params.push(`end_date=${encodeURIComponent(endDate)}`);
  } else {
    params.push(`months=${months}`);
  }
  if (machineIds && machineIds.length > 0) {
    params.push(`machine_ids=${encodeURIComponent(machineIds.join(','))}`);
  }
  if (excludeErrorTypes && excludeErrorTypes.length > 0) {
    params.push(`exclude_error_types=${encodeURIComponent(excludeErrorTypes.join(','))}`);
  }
  return api.get(`/error_sessions/summary?${params.join('&')}`);
};

// Every distinct error type ever recorded (for scope filters), with session counts.
export const getErrorTypes = () => {
  return api.get('/error_sessions/error_types');
};

export const searchErrorSessions = ({
  startTime,
  endTime,
  machineId = null,
  machineIds = null,
  errorType = null,
  excludeErrorTypes = null,
  page = 1,
  pageSize = 50,
} = {}) => {
  const params = [
    `start_time=${encodeURIComponent(startTime)}`,
    `end_time=${encodeURIComponent(endTime)}`,
    `page=${page}`,
    `page_size=${pageSize}`,
  ];
  if (machineId) {
    params.push(`machine_id=${machineId}`);
  }
  if (machineIds && machineIds.length > 0) {
    params.push(`machine_ids=${encodeURIComponent(machineIds.join(','))}`);
  }
  if (errorType) {
    params.push(`error_type=${encodeURIComponent(errorType)}`);
  }
  if (excludeErrorTypes && excludeErrorTypes.length > 0) {
    params.push(`exclude_error_types=${encodeURIComponent(excludeErrorTypes.join(','))}`);
  }
  return api.get(`/error_sessions/search?${params.join('&')}`);
};

export const searchUsageSessions = ({
  startTime,
  endTime,
  machineId = null,
  machineIds = null,
  page = 1,
  pageSize = 50,
} = {}) => {
  const params = [
    `start_time=${encodeURIComponent(startTime)}`,
    `end_time=${encodeURIComponent(endTime)}`,
    `page=${page}`,
    `page_size=${pageSize}`,
  ];
  if (machineId) {
    params.push(`machine_id=${machineId}`);
  }
  if (machineIds && machineIds.length > 0) {
    params.push(`machine_ids=${encodeURIComponent(machineIds.join(','))}`);
  }
  return api.get(`/usage_sessions/search?${params.join('&')}`);
};

export default api;
